import React, { useCallback, useEffect, useState } from 'react';
import { ApolloProvider } from '@apollo/client/react';
import { ServerError } from '@apollo/client';
import { GET_LOGIN_USER } from './apollo/queries';
import { getClient } from './apollo/configuration';
import { hasAdminAccess } from './utils/user';
import { isInSchoolEvent } from './utils/event';
import { useLocation } from 'react-router-dom';
import { useStateDispatch, useStateValue } from './context/AppContext';
import AppRouter from './AppRouter';
import CenterInfo from './components/nonDigital/landing/CenterInfo';
import EmergencyNotification from './components/common/emergency/EmergencyNotification';
import ErrorPage from './components/nonDigital/error/ErrorPage';
import get from 'lodash/get';
import Legal from './components/common/legal/Legal';
import LocalNav from './components/ui/navigation/LocalNav';
import OrgEventQueryContainer from './components/nonDigital/landing/OrgEventQueryContainer';
import Spinner from './components/ui/loading/SpinnerWrapper';
import StatusPanel from './components/common/status/StatusPanel';
import PreviewModeFrames from './components/ui/previewMode/PreviewModeFrames';
import { isPreview } from './utils/common';
import { setRumUserSession } from './rum';
import { ResizeProvider } from './context/ResizeContext';
import { IGetLoginUserData } from './apollo/types';
import { LocalUserState } from './types/LocalUserState';
import { LaunchDarklyProvider } from './components/LaunchDarklyProvider';

// Stable Error instances so a re-render doesn't pass a new reference and re-fire the
// `useEffect(error)` in ErrorPage (which would record duplicate RUM events).
const MISSING_USER_RECORD_ERROR = new Error('auth:missing_user_record');
const MISSING_TDTK_ASMT_TYPE_ERROR = new Error('data:missing_tdtkAsmtType');

/**
 * The App when "logged in" according to the window.CB object (util from Apricot).
 * This is wrapped by Gatekeeper. However, this component will still be gathering user details from
 * - global state (useStateValue)
 * - gql getLoginUser request
 *
 * It routes to one of the following:
 * - site/event selection page
 * - spinner if user data is still not available
 * - error page is user data is still bad
 * - legal attestation page if user is not confirmed
 * - authed routes
 */
export const ApolloApp = () => {
  const [client] = useState(getClient());
  const [, setLoadingUser] = useState(false);
  const { orgEvent, user } = useStateValue();
  const dispatch = useStateDispatch();
  const { pathname } = useLocation();

  const fetchUser = useCallback(async () => {
    setLoadingUser(true);
    // Push known org context up-front so a bootstrap failure still carries site/event in RUM.
    setRumUserSession({
      siteId: orgEvent?.siteId,
      eventId: orgEvent?.asmtEventId,
      asmtEventTypeCd: orgEvent?.asmtEventTypeCd,
      testCenterCd: orgEvent?.testCenterCd,
    });
    try {
      const { data } = await client.query<IGetLoginUserData>({
        fetchPolicy: 'network-only',
        query: GET_LOGIN_USER,
      });

      // Get login user
      const loginUser = get(data, 'viewer');
      if (loginUser) {
        setRumUserSession({
          userId: loginUser.id,
          userRole: loginUser.role,
          siteId: loginUser.site?.id,
          eventId: loginUser.site?.event?.asmtEventId,
          asmtEventTypeCd: loginUser.site?.event?.asmtEventTypeCd,
          testCenterCd: loginUser.site?.testCenterCd,
        });
        dispatch({
          orgEvent: {
            asmtEventId: loginUser.site.event.asmtEventId,
            asmtEventTypeCd: loginUser.site.event.asmtEventTypeCd,
            asmtId: loginUser.site.event.asmtId,
            eventDisplayDescr: loginUser.site.event.eventDisplayDescr,
            eventEndDt: loginUser.site.event.eventEndDt,
            eventStartDt: loginUser.site.event.eventStartDt,
            eventTitle: loginUser.site.event.eventTitle,
            orgId: loginUser.site.orgId,
            siteId: loginUser.site.id,
            tdtkAsmtType: loginUser.site.event.tdtkAsmtType,
            testCenterCd: loginUser.site.testCenterCd,
            // @ts-expect-error this is error is correct and a bug. there is no title on event, just eventTitle. Verify this field isn't used
            title: loginUser.site.event.title,
            //(ref: admin_event_payload_do_not_display_special_only)
            doNotDisplaySpecialUseOnlyDt: loginUser.site.event.doNotDisplaySpecialUseOnlyDt,
          },
          user: {
            active: loginUser.active,
            confirmed: loginUser.confirmed,
            email: loginUser.email,
            id: loginUser.id,
            room: loginUser.room
              ? {
                  id: loginUser?.room?.id || '-1',
                  title: loginUser?.room?.title || '',
                }
              : null,
            role: loginUser.role,
            iamRole: {
              // @ts-expect-error also unavailable
              rid: loginUser.rid,
              role: loginUser.role,
            },
            username: loginUser.username,
            welcomed: loginUser.welcomed,
          } as LocalUserState,
        });
        if (!loginUser.site.event?.tdtkAsmtType) {
          dispatch({
            globalError: (
              <ErrorPage
                errorCode='data:missing_tdtkAsmtType'
                error={MISSING_TDTK_ASMT_TYPE_ERROR}
                source='apollo-bootstrap'
                query='getLoginUser'
              />
            ),
          });
        }
      } else {
        throw new Error();
      }
    } catch (e) {
      const defaultErrorCode = '500';
      const handleDefaultError = (errorCode = defaultErrorCode) => {
        console.error('gatekeeper error', e);
        dispatch({
          globalError: <ErrorPage errorCode={errorCode} error={e} source='apollo-bootstrap' query='getLoginUser' />,
        });
      };
      // Apollo v4: ServerError is thrown directly
      const serverError: ServerError | undefined = ServerError.is(e) ? e : undefined;

      if (serverError) {
        // eslint-disable-next-line
        let parsedBody: any = {};
        try {
          parsedBody = JSON.parse(serverError?.bodyText);
        } catch {
          /* ignore */
        }
        const resErrorCode = parsedBody?.errors?.[0]?.code;
        const resStatusCode = serverError?.statusCode?.toString() || defaultErrorCode;
        if (
          ['auth:missing_user_role', 'auth:missing_admin_records', 'auth:missing_user_record'].includes(resErrorCode)
        ) {
          dispatch({
            globalError: (
              <ErrorPage
                errorCode='auth:missing_user_record'
                error={e}
                source='apollo-bootstrap'
                query='getLoginUser'
              />
            ),
            user: {
              missingUserRecord: true,
            },
          });
        } else {
          handleDefaultError(resStatusCode);
        }
      } else {
        handleDefaultError();
      }
    }

    setLoadingUser(false);
  }, [client, dispatch, orgEvent?.siteId, orgEvent?.asmtEventId, orgEvent?.asmtEventTypeCd, orgEvent?.testCenterCd]);

  useEffect(() => {
    // Fetch user every time siteId/orgId or asmtEventId changes. Triggered from OrgEventQueryContainer component
    if (orgEvent.asmtEventId && orgEvent.siteId && orgEvent.orgId) {
      fetchUser();
    }
  }, [orgEvent.asmtEventId, orgEvent.siteId, orgEvent.orgId, fetchUser]);

  let content: React.ReactNode = null;

  if (!(orgEvent.siteId && orgEvent.orgId && orgEvent.asmtEventId) || pathname === '/event') {
    // They aren't in a specific event role.
    content = (
      <ApolloProvider client={client}>
        <div className='container' id='main-content'>
          <OrgEventQueryContainer />
        </div>
      </ApolloProvider>
    );
  } else if (!user.id) {
    content = <Spinner />;
  } else if (user.missingUserRecord) {
    // Try clearing their session?
    sessionStorage.removeItem('orgEvent');

    // Failed to fetch a user for some reason.
    content = (
      <div className='container'>
        <ErrorPage errorCode='auth:missing_user_record' error={MISSING_USER_RECORD_ERROR} source='auth-missing' />
      </div>
    );
  } else if (!user.confirmed) {
    const agreementType = 'digitalLegal';
    content = (
      <ApolloProvider client={client}>
        <div className='container' id='main-content' tabIndex={-1}>
          <Legal legalAgreementType={agreementType} />
        </div>
      </ApolloProvider>
    );
  } else if (isInSchoolEvent(orgEvent) || (!hasAdminAccess(user?.rid, user?.role) && user?.room?.id)) {
    /**
     * In-School Digital Experience for all user types AND Digital Proctor Experience route.
     */
    content = (
      <ApolloProvider client={client}>
        <ResizeProvider>
          <LocalNav />
          {isPreview(user?.role, user?.mode, user?.active) && <PreviewModeFrames />}
          <div className='center-info py-2 hidden-md-up'>
            <CenterInfo />
          </div>
          <EmergencyNotification />
          <div id='main-content' tabIndex={-1}>
            <AppRouter />
          </div>
        </ResizeProvider>
      </ApolloProvider>
    );
  } else if (user.id) {
    // default view if user data has been fetched
    content = (
      <ApolloProvider client={client}>
        <ResizeProvider>
          <LocalNav />
          {isPreview(user?.role, user?.mode, user?.active) && <PreviewModeFrames />}
          <div className='center-info py-2 hidden-md-up'>
            <CenterInfo />
          </div>
          <EmergencyNotification />
          <StatusPanel />
          <div className='container' id='main-content' tabIndex={-1}>
            <AppRouter />
          </div>
        </ResizeProvider>
      </ApolloProvider>
    );
  } else {
    return null;
  }

  return (
    <LaunchDarklyProvider user={user} client={client}>
      {content}
    </LaunchDarklyProvider>
  );
};
